/**
 * Utilidades para determinar el estado de lectura del balance
 */

export interface BalanceReadStatus {
  text: string;
  icon: string;
  variant: 'success' | 'warning' | 'danger' | 'info';
  isAvailable: boolean;
}

/**
 * Mensajes informativos para el usuario sobre el estado del balance
 */
export const BALANCE_STATUS_MESSAGES = {
  NO_BALANCE_LIST: 'No hay balance cargado. Podrás crear uno manualmente en la siguiente pantalla.',
  NO_BALANCE_PAGE: 'No hay balance cargado. Podrás crear un balance manualmente en el paso "Carga de balance".',
  PROCESSING: 'El balance está en proceso. Por favor, espere a que esté disponible.',
  FAILED: 'El balance falló al procesarse. Podrás crear uno manualmente en la siguiente pantalla.',
} as const;

/**
 * Determina el estado de lectura del balance basado en processing_status
 * @param processingStatus - Estado de procesamiento del balance (MANUAL, COMPLETED, PROCESSING, FAILED, o null)
 * @returns Objeto con información del estado de lectura
 */
export const getBalanceReadStatus = (
  processingStatus: string | null | undefined
): BalanceReadStatus => {
  // Si no hay processingStatus (null o undefined), significa que no hay balance cargado
  // En este caso, permitimos acceso para que el usuario pueda crear un balance manualmente
  if (!processingStatus) {
    return {
      text: 'Sin balance',
      icon: 'help_circle_outline',
      variant: 'info',
      isAvailable: true,
    };
  }

  const status = processingStatus.toUpperCase();

  switch (status) {
    case 'MANUAL':
    case 'COMPLETED':
      return {
        text: 'Disponibles',
        icon: 'check_circle_outline',
        variant: 'success',
        isAvailable: true,
      };
    case 'PROCESSING':
      return {
        text: 'En proceso',
        icon: 'clock_outline',
        variant: 'warning',
        isAvailable: false,
      };
    case 'FAILED':
      return {
        text: 'No disponible',
        icon: 'error_circle_outline',
        variant: 'danger',
        isAvailable: true, // Permitir acceso para crear balance manual
      };
    default:
      return {
        text: 'No disponible',
        icon: 'error_circle_outline',
        variant: 'danger',
        isAvailable: true, // Permitir acceso para estados desconocidos también
      };
  }
};

/**
 * Mensajes por código de error del procesamiento del balance (i18n-ready).
 * Permite al front mostrar mensajes por idioma/tamaño.
 */
const BALANCE_ERROR_MESSAGES: Record<string, string> = {
  CUIT_MISMATCH: 'El CUIT del balance no coincide con el de la carpeta.',
  LLM_FORMAT_ERROR: 'No se pudo interpretar la respuesta del análisis.',
  PROCESSING_ERROR: 'Ocurrió un error al procesar el balance.',
};

/**
 * Obtiene el mensaje de error para mostrar al usuario a partir del código (y opcionalmente contexto).
 * @param errorCode - Código estructurado (ej. CUIT_MISMATCH, LLM_FORMAT_ERROR)
 * @param errorContext - JSON string opcional con contexto (ej. {"balanceCuit":"...","folderCuit":"..."})
 * @returns Mensaje listo para mostrar; si no hay código, mensaje genérico
 */
export const getBalanceProcessingErrorMessage = (
  errorCode: string | null | undefined,
  errorContext?: string | null
): string => {
  if (!errorCode) {
    return 'El balance falló al procesarse.';
  }
  const message = BALANCE_ERROR_MESSAGES[errorCode] ?? BALANCE_ERROR_MESSAGES.PROCESSING_ERROR;
  if (errorContext && errorCode === 'CUIT_MISMATCH') {
    try {
      const ctx = JSON.parse(errorContext) as { balanceCuit?: string; folderCuit?: string };
      if (ctx.balanceCuit != null && ctx.folderCuit != null) {
        return `El CUIT del balance (${ctx.balanceCuit}) no coincide con el de la carpeta (${ctx.folderCuit}).`;
      }
    } catch {
      // ignore invalid JSON
    }
  }
  return message;
};

